import { useQuery } from '@tanstack/react-query'
import type { V2GetStartupOutreachOutcomesResponse } from '@workspace/api-backend'
import { v2GetStartupOutreachOutcomesOptions } from '@workspace/api-backend/react-query'
import { StatCell } from '@workspace/ui/components/stat-cell'
import { TimeSeriesChart } from '@workspace/ui/components/time-series-chart'
import { cn } from '@workspace/ui/lib/utils'
import { CalendarCheck, Check, Handshake } from 'lucide-react'
import type { KeyboardEvent as ReactKeyboardEvent, RefCallback } from 'react'

/** The endpoint's success payload — the generated client exports no data alias. */
type OutcomesData = Extract<V2GetStartupOutreachOutcomesResponse, { status: 'ok' }>['data']

/**
 * Everything above the reply tables on the Outcomes page: what the campaigns
 * have produced (cards), how that accumulated (chart), and the outcomes a human
 * on our side has verified (list).
 *
 * All three read one endpoint, so the cards and the chart are the same numbers
 * bucketed differently and can never tell different stories.
 */
export function OutreachOutcomesSummary({
  startupId,
  onFocusOutcome,
}: {
  startupId: string
  /** Attaches to the newest confirmed outcome when the founder was sent to it. */
  onFocusOutcome?: RefCallback<HTMLElement>
}) {
  const query = useQuery(v2GetStartupOutreachOutcomesOptions({ path: { id: startupId } }))
  const data = query.data?.status === 'ok' ? query.data.data : null

  // Only the load itself hides the dashboard. A campaign that hasn't sent yet
  // still shows its cards at zero: "nothing has happened yet" is the answer the
  // founder came for, and an empty page doesn't give it.
  if (!data) return null

  return (
    <div className="mb-10 space-y-6">
      <div>
        <OutcomeStats totals={data.totals} />
        {data.totals.connectionsSent === 0 && (
          <p className="mt-2 text-[12.5px] text-muted-foreground">
            Nothing has gone out yet. These fill in as soon as your campaign starts sending.
          </p>
        )}
      </div>
      {data.daily.length > 0 && <OutcomeChart daily={data.daily} />}
      {data.confirmed.length > 0 && (
        <ConfirmedOutcomes confirmed={data.confirmed} onFocusFirst={onFocusOutcome} />
      )}
    </div>
  )
}

function OutcomeStats({ totals }: { totals: OutcomesData['totals'] }) {
  // Acceptance and reply rates are the two the founder actually reads as a
  // verdict on the campaign, so they sit under their own number as context
  // rather than taking up cells of their own.
  const pct = (part: number, whole: number) =>
    whole > 0 ? `${Math.round((part / whole) * 100)}% of sent` : null
  const outcomes = totals.callsBooked + totals.warmIntrosOffered

  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/40 bg-border/40 md:grid-cols-5">
      <StatCell
        value={String(totals.connectionsSent)}
        label="Connections sent"
        sub="Requests sent from your own LinkedIn"
      />
      <StatCell
        value={String(totals.connectionsAccepted)}
        label="Connections accepted"
        sub={pct(totals.connectionsAccepted, totals.connectionsSent) ?? 'Waiting on the first one'}
      />
      <StatCell
        value={String(totals.profileViews)}
        label="Profile views"
        sub={
          totals.returnVisits > 0
            ? `${totals.returnVisits} came back for a second look`
            : 'Investors who read your SeedForge profile'
        }
      />
      <StatCell
        value={String(totals.replies)}
        label="Replied"
        sub={pct(totals.replies, totals.connectionsSent) ?? 'Waiting on the first one'}
      />
      <StatCell
        value={String(outcomes)}
        label="Outcomes"
        sub={
          outcomes > 0
            ? `${totals.callsBooked} calls · ${totals.warmIntrosOffered} warm intros`
            : 'Calls booked and warm intros offered'
        }
        accent={outcomes > 0}
      />
    </div>
  )
}

// Sent leads the eye, so it takes the neutral chart-1; the two that matter most
// (replies, outcomes) get the warm end of the scale.
const OUTCOME_SERIES = [
  { key: 'connectionsSent', label: 'Sent', color: 'var(--chart-1)', kind: 'area' as const },
  {
    key: 'connectionsAccepted',
    label: 'Accepted',
    color: 'var(--chart-2)',
    kind: 'area' as const,
  },
  { key: 'profileViews', label: 'Profile views', color: 'var(--chart-4)', kind: 'area' as const },
  { key: 'replies', label: 'Replies', color: 'var(--chart-3)', kind: 'area' as const },
  { key: 'outcomes', label: 'Outcomes', color: 'var(--chart-5)', kind: 'line' as const },
]

function OutcomeChart({ daily }: { daily: OutcomesData['daily'] }) {
  return (
    <section className="rounded-2xl border border-border/40 bg-card/20 px-5 py-5">
      <h2 className="mb-4 font-semibold text-[15px] text-foreground">Day by day</h2>
      <TimeSeriesChart data={daily} series={OUTCOME_SERIES} />
    </section>
  )
}

function ConfirmedOutcomes({
  confirmed,
  onFocusFirst,
}: {
  confirmed: OutcomesData['confirmed']
  onFocusFirst?: RefCallback<HTMLElement>
}) {
  return (
    <section>
      <h2 className="mb-1 font-semibold text-[15px] text-foreground">Confirmed outcomes</h2>
      <p className="mb-3 text-[13px] text-muted-foreground">
        Reviewed and verified by our team before you're ever charged for them.
      </p>
      <div className="space-y-2">
        {confirmed.map((outcome, index) => (
          // The whole card opens the conversation, exactly like a reply row —
          // one behaviour for "take this over in LinkedIn", not two.
          <div
            key={outcome.id}
            // Newest first, so the top card is the outcome the founder was
            // just told about.
            ref={index === 0 ? onFocusFirst : undefined}
            {...(outcome.conversationUrl
              ? {
                  role: 'link' as const,
                  tabIndex: 0,
                  onClick: () =>
                    window.open(outcome.conversationUrl as string, '_blank', 'noopener,noreferrer'),
                  onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      window.open(
                        outcome.conversationUrl as string,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  },
                }
              : {})}
            className={cn(
              'rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] px-4 py-3 transition-colors',
              outcome.conversationUrl &&
                'cursor-pointer hover:bg-emerald-500/[0.08] focus-visible:bg-emerald-500/[0.08] focus-visible:outline-none'
            )}
          >
            <div className="flex items-center gap-2">
              <Check className="size-4 shrink-0 text-emerald-400" />
              <p className="font-semibold text-[14px] text-foreground">
                {outcome.kind === 'call' ? 'Call secured' : 'Warm intro offered'} ·{' '}
                {outcome.contactName ? `${outcome.contactName}, ` : ''}
                {outcome.investorName}
              </p>
            </div>
            {outcome.summary && (
              <p className="mt-1 pl-6 text-[13px] text-foreground/75 italic leading-relaxed">
                {outcome.summary}
              </p>
            )}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 pl-6">
              <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                {outcome.kind === 'call' ? (
                  <CalendarCheck className="size-3.5" />
                ) : (
                  <Handshake className="size-3.5" />
                )}
                Confirmed{' '}
                {new Date(outcome.confirmedAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </p>
              {outcome.freeDuringTrial && (
                <span className="font-mono text-[11.5px] text-emerald-400">
                  Free during your trial
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
